import {
  useMemo, useRef, useState,
} from 'react';
import { useTranslation } from 'react-i18next';

// Utils
import groupOptionsByOptGroup from '../../../utils/optGroup/optGroup';

// Domain
import { filterDetailTabsBySearch } from '../../../domain/detail/detailTabsManagementUseCase';

// Components
import {
  PopoverContent,
  PopoverTrigger,
} from '../../atoms/Popover/Popover';
import { Popover } from '../../../store/popover/PopoverContext';
import IconButton from '../../atoms/IconButton/IconButton';
import SearchListItem from '../../molecules/SearchListItem/SearchListItem';
import SearchGroupList from '../../molecules/SearchGroupList/SearchGroupList';
import ColumnsVisibility from '../../molecules/ColumnsVisibility/ColumnsVisibility';
import Checkbox from '../../atoms/Checkbox/Checkbox';
import Button from '../../atoms/Button/Button';
import SearchField from '../../molecules/SearchField/SearchField';
import CustomVueListContainer, { CustomVueList } from '../CustomVueList/CustomVueList';

// Styles
import styles from './DetailTabsManagementPopover.module.css';

/**
 * Popover for managing detail tabs at a given level.
 * @param {object} props
 * @param {DetailTabItem[]} props.candidates - Available tabs not yet used in the bar
 * @param {DetailTabItem[]} props.allItems - All tabs at the current level
 * @param {(tabId: string) => void} props.onSelectItem - Add tab handler (simple list view)
 * @param {(tabId: string, available: boolean) => void} props.onToggleAvailable - Toggle availability (settings view)
 * @param {() => void} props.onApply - Apply settings changes
 * @param {() => void} props.onEnterSettings - Start config draft when entering settings view
 * @param {() => void} props.onCancel - Discard config draft and close
 * @param {number} [props.level=0] - Tab row level (0 = dark root row, >0 = light/gray rows)
 * @returns {JSX.Element}
 */
function DetailTabsManagementPopover({
  candidates,
  allItems,
  onSelectItem,
  onToggleAvailable,
  onApply,
  onEnterSettings,
  onCancel,
  level = 0,
}) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [isSettingsView, setIsSettingsView] = useState(false);
  const [search, setSearch] = useState('');
  const skipCancelOnCloseRef = useRef(false);

  const filteredCandidates = useMemo(
    () => filterDetailTabsBySearch(candidates, search),
    [candidates, search],
  );

  const filteredAllItems = useMemo(
    () => filterDetailTabsBySearch(allItems, search),
    [allItems, search],
  );

  const groupedCandidates = useMemo(
    () => groupOptionsByOptGroup(
      filteredCandidates.map((item) => ({ ...item, lib: item.label })),
    ),
    [filteredCandidates],
  );

  const handleOpenChange = (open) => {
    setIsOpen(open);

    if (!open) {
      setIsSettingsView(false);
      setSearch('');

      if (!skipCancelOnCloseRef.current) {
        onCancel();
      }

      skipCancelOnCloseRef.current = false;
    }
  };

  const handleSelectItem = (tabId) => {
    onSelectItem(tabId);
    skipCancelOnCloseRef.current = true;
    setIsOpen(false);
    setIsSettingsView(false);
    setSearch('');
  };

  const handleCancel = () => {
    setIsSettingsView(false);
    setSearch('');
    setIsOpen(false);
    onCancel();
  };

  const handleApply = () => {
    onApply();
    skipCancelOnCloseRef.current = true;
    setIsSettingsView(false);
    setSearch('');
    setIsOpen(false);
  };

  const handleToggleSettingsView = () => {
    if (isSettingsView) {
      onCancel();
      setIsSettingsView(false);
    } else {
      onEnterSettings();
      setIsSettingsView(true);
    }
  };

  const renderSimpleList = () => (
    <CustomVueList>
      <ColumnsVisibility
        options={groupedCandidates}
        messageNoOptions={search.trim()
          ? t('detail.tabsManagement.noResult')
          : 'Aucun onglet disponible'}
      >
        {(label, options) => (
          <SearchGroupList
            key={label}
            hasLabel={!!label}
            items={options}
            label={label}
          >
            {(item) => (
              <SearchListItem
                key={item.id}
                id={item.id}
                onClick={() => handleSelectItem(item.id)}
              >
                <p className="padding-right-m padding-left-m cursor-pointer">{item.label}</p>
              </SearchListItem>
            )}
          </SearchGroupList>
        )}
      </ColumnsVisibility>
    </CustomVueList>
  );

  const renderSettingsList = () => (
    <div className={styles.list}>
      {filteredAllItems.length === 0 && search.trim() ? (
        <p className={styles['empty-message']}>{t('detail.tabsManagement.noResult')}</p>
      ) : null}
      {filteredAllItems.map((item) => (
        <div key={item.id} className={styles['settings-item']}>
          <Checkbox
            id={`detail-tab-available-${item.id}`}
            checked={item.available}
            onChange={() => onToggleAvailable(item.id, !item.available)}
            size="small"
            theme="black"
            label={item.label}
          />
        </div>
      ))}
    </div>
  );

  return (
    <Popover
      open={isOpen}
      onOpenChange={handleOpenChange}
      placement="bottom-start"
    >
      <PopoverTrigger asChild>
        <IconButton
          icon="setting-5"
          color={level === 0 ? 'no-background-white' : 'no-background'}
          className={level > 0 ? styles['trigger-on-gray-row'] : undefined}
          size="medium"
          onClick={() => setIsOpen(!isOpen)}
          title="Gérer les onglets"
        />
      </PopoverTrigger>
      <PopoverContent>
        <CustomVueListContainer>
          <div className={styles.container}>
            <div className={styles.header}>
              <h3 className={styles['header-title']}>
                {isSettingsView
                  ? t('detail.tabsManagement.addTabsToMyList')
                  : t('detail.tabsManagement.goToTab')}
              </h3>
              <IconButton
                icon="setting-2"
                color="transparent"
                size="small"
                onClick={handleToggleSettingsView}
                title={isSettingsView ? 'Liste des onglets disponibles' : 'Configurer les onglets'}
              />
            </div>

            <div className={styles.search}>
              <SearchField
                id={`detail-tabs-search-${level}`}
                placeholder={t('detail.tabsManagement.search')}
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                onFocus={() => {}}
              />
            </div>

            {isSettingsView ? renderSettingsList() : renderSimpleList()}

            {isSettingsView ? (
              <div className={styles.footer}>
                <Button
                  color="primary100"
                  size="small"
                  onClick={handleCancel}
                >
                  Annuler
                </Button>

                <Button
                  size="small"
                  onClick={handleApply}
                >
                  Appliquer
                </Button>
              </div>
            ) : null}
          </div>
        </CustomVueListContainer>
      </PopoverContent>
    </Popover>
  );
}

export default DetailTabsManagementPopover;
